import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { cartService, Cart, CartItem, AddToCartRequest } from '@/services/cartService';

/**
 * Cart State Interface
 */
interface CartState {
  cart: Cart | null;
  isLoading: boolean;
  error: string | null;
  itemCount: number;
}

/**
 * Initial state
 */
const initialState: CartState = {
  cart: null,
  isLoading: false,
  error: null,
  itemCount: 0,
};

/**
 * Fetch user's cart
 */
export const fetchCart = createAsyncThunk<Cart, void, { rejectValue: string }>(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartService.getCart();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch cart'
      );
    }
  }
);

/**
 * Add item to cart
 */
export const addToCart = createAsyncThunk<
  Cart,
  AddToCartRequest,
  { rejectValue: string }
>('cart/addToCart', async (itemData, { rejectWithValue }) => {
  try {
    const response = await cartService.addToCart(itemData);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to add item to cart'
    );
  }
});

/**
 * Update item quantity
 */
export const updateItemQuantity = createAsyncThunk<
  Cart,
  { itemIndex: number; quantity: number },
  { rejectValue: string }
>('cart/updateItemQuantity', async ({ itemIndex, quantity }, { rejectWithValue }) => {
  try {
    const response = await cartService.updateItemQuantity(itemIndex, quantity);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to update item quantity'
    );
  }
});

/**
 * Remove item from cart
 */
export const removeItem = createAsyncThunk<
  Cart,
  number,
  { rejectValue: string }
>('cart/removeItem', async (itemIndex, { rejectWithValue }) => {
  try {
    const response = await cartService.removeItem(itemIndex);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to remove item from cart'
    );
  }
});

/**
 * Clear cart
 */
export const clearCart = createAsyncThunk<void, void, { rejectValue: string }>(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      await cartService.clearCart();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to clear cart'
      );
    }
  }
);

/**
 * Cart slice
 */
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCart: (state, action: PayloadAction<Cart | null>) => {
      state.cart = action.payload;
      state.itemCount = action.payload?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCart: (state) => {
      state.cart = null;
      state.itemCount = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch cart
    builder
      .addCase(fetchCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cart = action.payload;
        state.itemCount = action.payload.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch cart';
      });

    // Add to cart
    builder
      .addCase(addToCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cart = action.payload;
        state.itemCount = action.payload.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to add item to cart';
      });

    // Update item quantity
    builder
      .addCase(updateItemQuantity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateItemQuantity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cart = action.payload;
        state.itemCount = action.payload.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      })
      .addCase(updateItemQuantity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update item quantity';
      });

    // Remove item
    builder
      .addCase(removeItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cart = action.payload;
        state.itemCount = action.payload.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      })
      .addCase(removeItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to remove item from cart';
      });

    // Clear cart
    builder
      .addCase(clearCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.isLoading = false;
        state.cart = null;
        state.itemCount = 0;
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to clear cart';
      });
  },
});

// Export actions
export const { setCart, clearError, resetCart } = cartSlice.actions;

// Export selectors
export const selectCart = (state: { cart: CartState }) => state.cart.cart;
export const selectCartLoading = (state: { cart: CartState }) => state.cart.isLoading;
export const selectCartError = (state: { cart: CartState }) => state.cart.error;
export const selectCartItemCount = (state: { cart: CartState }) => state.cart.itemCount;

export default cartSlice.reducer;








